import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  X,
  ChevronLeft,
  ChevronRight,
  FileDown,
  Filter,
  Loader2,
} from "lucide-react";
import { api } from "@/api/client";
import { STATUS_COLORS, CONFIDENTIALITY_LABELS, CONFIDENTIALITY_COLORS } from "@/types/document";
import type { Document, DocumentListResponse, DocumentStatus, ConfidentialityLevel } from "@/types/document";

interface CompassDocumentTableProps {
  title: string;
  jenisId?: string;
  siklusFilter?: string;
  initialStatusFilter?: string;
  onClose: () => void;
  onDocClick?: (id: string) => void;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function CompassDocumentTable({
  title,
  jenisId,
  siklusFilter,
  initialStatusFilter,
  onClose,
  onDocClick,
}: CompassDocumentTableProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter || "");
  const [showFilters, setShowFilters] = useState(!!initialStatusFilter);
  const pageSize = 10;

  const { data, isLoading } = useQuery<DocumentListResponse>({
    queryKey: ["documents", "compass-table", jenisId, siklusFilter, statusFilter, page],
    queryFn: () =>
      api.get("/documents/", {
        page,
        page_size: pageSize,
        ...(jenisId ? { jenis_pnbp_id: jenisId } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      }),
  });

  const handleExportCSV = () => {
    if (!data?.items) return;
    const header = "No,Nama Dokumen,Siklus,Bentuk,Dimensi,Status,Kerahasiaan,Tanggal\n";
    const rows = data.items
      .map(
        (doc, i) =>
          `${i + 1},"${doc.judul}","${doc.siklus_keuangan_nama}","${doc.bentuk_dokumen_nama}","${doc.dimensi?.map((d) => d.nama).join("; ") || "-"}","${doc.status_nama}","${doc.tingkat_kerahasiaan}","${formatDate(doc.tanggal_dokumen)}"`,
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dokumen_pnbp_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white transition-colors"
          >
            <FileDown className="h-3.5 w-3.5" />
            Download CSV
          </button>
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              showFilters || statusFilter
                ? "border-blue-300 bg-blue-50 text-blue-700"
                : "border-gray-300 text-gray-600 hover:bg-white"
            }`}
          >
            <Filter className="h-3.5 w-3.5" />
            Filter
          </button>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-gray-200 transition-colors"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="flex items-center gap-3 px-5 py-2.5 bg-blue-50/50 border-b border-gray-200">
          <span className="text-xs font-medium text-gray-500">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-2.5 py-1 text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Semua Status</option>
            <option value="DRAFT">Draft</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="REVIEW">Review</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="ACTIVE">Aktif</option>
            <option value="EXPIRED">Expired</option>
          </select>
          {statusFilter && (
            <button
              onClick={() => { setStatusFilter(""); setPage(1); }}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Reset Filter
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
          <span className="ml-2 text-sm text-gray-500">Memuat data...</span>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">
                    No
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Nama Dokumen
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Siklus
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Bentuk
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Dimensi
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Tgl Terbit
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Akses
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.items ?? []).map((doc, index) => (
                  <tr
                    key={doc.id}
                    onClick={() => onDocClick ? onDocClick(doc.id) : navigate(`/documents/${doc.id}`)}
                    className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2.5 text-gray-500">
                      {(page - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-gray-900 truncate max-w-[300px]">{doc.judul}</p>
                      {doc.nomor_dokumen && (
                        <p className="text-xs text-gray-400 font-mono">{doc.nomor_dokumen}</p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">{doc.siklus_keuangan_nama}</td>
                    <td className="px-4 py-2.5 text-gray-600">{doc.bentuk_dokumen_nama}</td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {doc.dimensi?.length
                        ? doc.dimensi.map((d) => d.nama).join(", ")
                        : "-"}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">
                      {formatDate(doc.tanggal_dokumen)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          STATUS_COLORS[doc.status_kode as DocumentStatus] || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {doc.status_nama}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          CONFIDENTIALITY_COLORS[doc.tingkat_kerahasiaan as ConfidentialityLevel] ||
                          "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {CONFIDENTIALITY_LABELS[doc.tingkat_kerahasiaan as ConfidentialityLevel] ||
                          doc.tingkat_kerahasiaan}
                      </span>
                    </td>
                  </tr>
                ))}
                {(data?.items ?? []).length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-400">
                      Tidak ada dokumen
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50/50">
              <span className="text-xs text-gray-500">
                Menampilkan {(page - 1) * pageSize + 1}-
                {Math.min(page * pageSize, data.total)} dari {data.total} dokumen
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-lg p-1.5 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs font-medium text-gray-600 px-2">
                  {page} / {data.total_pages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                  disabled={page >= data.total_pages}
                  className="rounded-lg p-1.5 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
